import { intersperse } from "@/client/react";
import { useUserSetting } from "@/client/ui/hooks/useUserSetting";
import { splitHanziText } from "@/data/hanzi";
import type {
  HanziWordToPinyinTypedQuestion,
  MistakeType,
  UnsavedSkillRating,
} from "@/data/model";
import { QuestionFlagKind } from "@/data/model";
import type {
  PinyinUnitSuggestion,
  PinyinUnitSuggestions,
} from "@/data/pinyin";
import { pinyinUnitSuggestions } from "@/data/pinyin";
import type { HanziToPinyinTypedQuestionRating } from "@/data/questions/hanziWordToPinyinTyped";
import {
  gradeHanziToPinyinTypedQuestion,
  shouldAutoSubmitPinyinTypedAnswer,
} from "@/data/questions/hanziWordToPinyinTyped";
import { hanziWordFromSkill } from "@/data/skills";
import { autoCheckUserToggleSetting } from "@/data/userSettings";
import { hanziFromHanziWord } from "@/dictionary";
import { emptyArray } from "@pinyinly/lib/collections";
import type { ReactNode, Ref } from "react";
import { useRef, useState } from "react";
import { useQuizTimer } from "@/client/ui/hooks/useQuizTimer";
import type { TextInputInstance } from "react-native";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import Reanimated, { FadeIn, FadeOut } from "react-native-reanimated";
import type { DeepReadonly } from "ts-essentials";
import { PinyinOptionButton } from "./PinyinOptionButton";
import { QuizFlagText } from "./QuizFlagText";
import { QuizDeckQuestionSkeleton } from "./QuizDeckQuestionSkeleton";
import { TextAnswerInputSingle } from "./TextAnswerInputSingle";
import type { TextAnswerInputSingleState } from "./TextAnswerInputSingle.utils";
import { ratingToInputState } from "./TextAnswerInputSingle.utils";

export function QuizDeckHanziWordToPinyinTypedQuestion({
  noAutoFocus = true,
  question,
  onNext,
  onChangeAnswer,
  onRating,
}: {
  noAutoFocus?: boolean;
  question: HanziWordToPinyinTypedQuestion;
  onNext: () => void;
  onChangeAnswer: () => void;
  onRating: (
    ratings: UnsavedSkillRating[],
    mistakes: readonly MistakeType[],
  ) => void;
}) {
  const { skill, flag, answers, bannedMeaningPinyinHint } = question;

  const autoCheck =
    useUserSetting({ setting: autoCheckUserToggleSetting }).value?.enabled ??
    false;

  const userAnswerRef = useRef(``);
  const [isUserAnswerEmpty, setIsUserAnswerEmpty] = useState(true);
  const [rating, setRating] = useState<HanziToPinyinTypedQuestionRating>();

  const timer = useQuizTimer();
  const hanziCharacters = splitHanziText(
    hanziFromHanziWord(hanziWordFromSkill(skill)),
  );

  const submit = () => {
    // First press checks your answer; the next one moves to the next question.
    if (rating == null) {
      const durationMs = timer.getDurationMs();
      const questionRating = gradeHanziToPinyinTypedQuestion(
        question,
        userAnswerRef.current,
        durationMs,
      );

      setRating(questionRating);
      onRating(
        questionRating.skillRatings,
        questionRating.correct ? emptyArray : questionRating.mistakes,
      );
    } else {
      onNext();
    }
  };

  return (
    <QuizDeckQuestionSkeleton
      rating={rating}
      isUserAnswerProvided={!isUserAnswerEmpty}
      onSubmit={submit}
      onChangeAnswer={onChangeAnswer}
      showIdkButton
      skill={skill}
    >
      <View
        className={
          // 200px is the space left above a text input on mobile when the
          // on-screen keyboard is open (e.g. on iOS). By setting it 200px it
          // means only this element will remain visible when the keyboard is
          // open. It creates a clean look by completely hiding the content above
          // it.
          `h-50 justify-between`
        }
      >
        <View>
          {flag == null ? null : <QuizFlagText flag={flag} />}
          <View>
            <Text className="font-sans text-xl font-bold text-fg-loud">
              {flag?.kind === QuestionFlagKind.OtherAnswer
                ? `What is the other pronunciation?`
                : `What sound does this make?`}
            </Text>
          </View>
        </View>
        <View className="flex-row justify-center gap-2 pb-3">
          {hanziCharacters.map((hanzi, i) => {
            return (
              <View className="items-center gap-2" key={i}>
                <Text className="font-sans text-[80px] font-medium text-fg-loud">
                  {hanzi}
                </Text>
              </View>
            );
          })}
        </View>
        <View
          // Invisible element at the bottom so that the `justify-between`
          // applys space at the bottom too, whilst still keeping this whole
          // thing at 200px.
          className="h-0"
        />
      </View>
      <PinyinTextInputSingle
        autoFocus={!noAutoFocus}
        editable={rating == null}
        onChangeText={(text, suggestionAccepted) => {
          userAnswerRef.current = text;
          setIsUserAnswerEmpty(text.trim().length === 0);

          if (
            autoCheck &&
            shouldAutoSubmitPinyinTypedAnswer(
              text,
              skill,
              answers,
              suggestionAccepted,
            )
          ) {
            submit();
          }
        }}
        hintText={
          bannedMeaningPinyinHint.length > 0 ? (
            <>
              You have already answered{` `}
              {intersperse(
                bannedMeaningPinyinHint.map((pinyin, i) => (
                  <Text className="pyly-bold" key={i}>
                    {pinyin}
                  </Text>
                )),
                <Text> and </Text>,
              )}
              .
            </>
          ) : undefined
        }
        onSubmit={submit}
        state={ratingToInputState(isUserAnswerEmpty, rating?.rating)}
      />
    </QuizDeckQuestionSkeleton>
  );
}

const PinyinTextInputSingle = ({
  autoFocus,
  disabled = false,
  editable,
  inputRef,
  onChangeText,
  onSubmit,
  hintText,
  state = `default`,
}: {
  autoFocus: boolean;
  disabled?: boolean;
  editable?: boolean;
  inputRef?: Ref<TextInputInstance>;
  onChangeText: (text: string, suggestionAccepted: boolean) => void;
  onSubmit: () => void;
  hintText?: ReactNode;
  state?: TextAnswerInputSingleState;
}) => {
  const [text, setText] = useState(``);
  const isEditable = editable ?? !disabled;

  const suggestions = isEditable ? pinyinUnitSuggestions(text) : null;

  const updateText = (newText: string, suggestionAccepted: boolean) => {
    setText(newText);
    onChangeText(newText, suggestionAccepted);
  };

  const handleChangeText = (newText: string) => {
    if (suggestions !== null) {
      for (const option of suggestions.units) {
        if (newText.endsWith(option.tone.toString())) {
          acceptSuggestion(suggestions, option);
          return;
        }
      }
    }

    updateText(newText, false);
  };

  const acceptSuggestion = (
    suggestions: DeepReadonly<PinyinUnitSuggestions>,
    unit: PinyinUnitSuggestion,
  ) => {
    const newText =
      text.slice(0, suggestions.from) +
      unit.pinyinUnit +
      text.slice(suggestions.to);
    updateText(newText, true);
  };

  return (
    <View className="gap-2">
      <TextAnswerInputSingle
        autoFocus={autoFocus}
        disabled={disabled}
        editable={editable}
        inputRef={inputRef}
        onChangeValue={handleChangeText}
        onSubmit={onSubmit}
        placeholder="Type in pinyin"
        state={state}
        value={text}
      />
      <View className="flex-row flex-wrap justify-center gap-2">
        {hiddenPlaceholderOptions}
        <View className="absolute inset-0 flex-row flex-wrap content-start justify-center gap-2">
          {suggestions == null ? (
            hintText == null ? null : (
              <Text className="pyly-body-caption">{hintText}</Text>
            )
          ) : (
            suggestions.units.map((unit) => (
              <Reanimated.View
                key={`${unit.pinyinUnit}-${unit.tone}`}
                entering={FadeIn.duration(200)}
                exiting={FadeOut.duration(100)}
              >
                <PinyinOptionButton
                  pinyin={unit.pinyinUnit}
                  shortcutKey={unit.tone.toString()}
                  active={unit.tone === suggestions.currentTone}
                  onPress={() => {
                    acceptSuggestion(suggestions, unit);
                  }}
                />
              </Reanimated.View>
            ))
          )}
        </View>
      </View>
    </View>
  );
};

// Reserve the space
const hiddenPlaceholderOptions = (
  <>
    <PinyinOptionButton pinyin="xxxxxx" shortcutKey="x" className="invisible" />
    <PinyinOptionButton pinyin="xxxxxx" shortcutKey="x" className="invisible" />
    <PinyinOptionButton pinyin="xxxxxx" shortcutKey="x" className="invisible" />
    <PinyinOptionButton pinyin="xxxxxx" shortcutKey="x" className="invisible" />
    <PinyinOptionButton pinyin="xxxxxx" shortcutKey="x" className="invisible" />
  </>
);
